import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { SafeResourceUrl } from '@angular/platform-browser';
import { FilesResponse } from '../../../services/file.service';
import { BundleViewerService } from '../../../services/bundle-viewer.service';
import { YoutubeEmbedComponent } from '../youtube-embed/youtube-embed.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';

export type MindmapFormat = 'html' | 'imindmap' | 'pdf' | 'png' | 'txt' | 'youtube';
type ViewportSize = 'phone' | 'tablet' | 'laptop' | 'desktop';

interface ViewerState {
  url?: SafeResourceUrl;
  loading: boolean;
  error?: string;
  langActuallyServed?: 'en' | 'hi';
}

@Component({
  selector: 'app-mindmap-viewer',
  standalone: true,
  imports: [CommonModule, YoutubeEmbedComponent, EmptyStateComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="mv" [class.mv--lazy]="!visible" [attr.data-size]="viewportSize">
      <div class="mv__tabs" *ngIf="formats.length > 1" role="tablist">
        <button *ngFor="let f of formats; trackBy: trackFormat"
                role="tab"
                type="button"
                [attr.aria-selected]="f === activeFormat"
                [class.mv__tab--active]="f === activeFormat"
                class="mv__tab"
                (click)="setFormat(f)">
          <span class="material-icons mv__tabIcon" aria-hidden="true">{{ iconFor(f) }}</span>
          <span class="mv__tabLabel">{{ labelFor(f) }}</span>
        </button>
        <span class="mv__tabs-spacer"></span>
        <button *ngIf="canFullscreen()"
                type="button"
                class="mv__fs"
                (click)="fullscreen.emit()"
                aria-label="Open fullscreen">
          <span class="material-icons" aria-hidden="true">fullscreen</span>
        </button>
      </div>

      <div class="mv__stage" #stage>
        <ng-container [ngSwitch]="activeFormat">
          <ng-container *ngSwitchCase="'youtube'">
            <app-youtube-embed *ngIf="bundle.youtube" [youtube]="bundle.youtube"></app-youtube-embed>
          </ng-container>

          <ng-container *ngSwitchDefault>
            <app-empty-state *ngIf="state().loading" kind="loading" message="Loading mindmap…"></app-empty-state>
            <app-empty-state *ngIf="state().error" kind="error" [message]="state().error"></app-empty-state>

            <iframe *ngIf="state().url && (activeFormat === 'html' || activeFormat === 'imindmap')"
                    class="mv__frame"
                    [src]="state().url!"
                    [title]="bundle.topic || 'Mindmap'"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                    referrerpolicy="no-referrer"
                    loading="lazy"></iframe>

            <iframe *ngIf="state().url && activeFormat === 'pdf'"
                    class="mv__frame"
                    [src]="state().url!"
                    [title]="(bundle.topic || 'Document') + ' (PDF)'"
                    sandbox="allow-scripts allow-same-origin"
                    referrerpolicy="no-referrer"
                    loading="lazy"></iframe>

            <img *ngIf="state().url && activeFormat === 'png'"
                 class="mv__img"
                 [src]="state().url!"
                 [alt]="bundle.topic || ''"
                 loading="lazy"
                 decoding="async">

            <pre *ngIf="state().url && activeFormat === 'txt'" class="mv__text">{{ pendingTextContent }}</pre>
          </ng-container>
        </ng-container>
      </div>

      <p class="mv__langNotice" *ngIf="state().url && state().langActuallyServed && state().langActuallyServed !== lang">
        Showing English — Hindi version not available for this format.
      </p>
    </div>
  `,
  styleUrls: ['./mindmap-viewer.component.css'],
})
export class MindmapViewerComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) bundle!: FilesResponse;
  @Input({ required: true }) formats: MindmapFormat[] = [];
  @Input() lang: 'en' | 'hi' = 'en';
  @Input() viewportSize: ViewportSize = 'laptop';
  @Input() preload: 'eager' | 'visible' = 'visible';
  @Output() fullscreen = new EventEmitter<void>();

  @ViewChild('stage', { static: true }) stage?: ElementRef<HTMLElement>;

  activeFormat: MindmapFormat = 'html';
  visible = false;
  pendingTextContent = '';
  private observer?: IntersectionObserver;
  private states = new Map<MindmapFormat, ViewerState>();
  private subscriptions = new Map<MindmapFormat, Subscription>();
  private cdr = inject(ChangeDetectorRef);
  private viewer = inject(BundleViewerService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formats'] && this.formats.length) {
      if (!this.formats.includes(this.activeFormat)) {
        this.activeFormat = this.formats[0];
      }
    }
    if (changes['lang']) {
      this.releaseAll();
    }
    if (this.preload === 'eager' && !this.visible) {
      this.visible = true;
      this.fetchActive();
    } else if (this.preload === 'visible' && !this.observer) {
      queueMicrotask(() => this.armObserver());
    } else if (this.visible) {
      this.fetchActive();
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.releaseAll();
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  setFormat(f: MindmapFormat): void {
    if (f === this.activeFormat) return;
    this.activeFormat = f;
    if (this.visible) this.fetchActive();
  }

  state(): ViewerState {
    return this.states.get(this.activeFormat) ?? { loading: false };
  }

  trackFormat = (_: number, f: MindmapFormat): string => f;

  iconFor(f: MindmapFormat): string {
    switch (f) {
      case 'html': return 'account_tree';
      case 'imindmap': return 'hub';
      case 'pdf': return 'picture_as_pdf';
      case 'png': return 'image';
      case 'txt': return 'subject';
      case 'youtube': return 'play_circle';
    }
  }

  labelFor(f: MindmapFormat): string {
    switch (f) {
      case 'html': return 'Mindmap';
      case 'imindmap': return 'iMindMap';
      case 'pdf': return 'PDF';
      case 'png': return 'Image';
      case 'txt': return 'Outline';
      case 'youtube': return 'Video';
    }
  }

  canFullscreen(): boolean {
    return this.viewportSize === 'desktop' && (this.activeFormat === 'html' || this.activeFormat === 'imindmap' || this.activeFormat === 'pdf');
  }

  private armObserver(): void {
    if (!this.stage) {
      this.visible = true;
      this.fetchActive();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e?.isIntersecting) return;
        this.visible = true;
        this.observer?.disconnect();
        this.fetchActive();
        this.cdr.markForCheck();
      },
      { rootMargin: this.preload === 'eager' ? '200px' : '0px' }
    );
    this.observer.observe(this.stage.nativeElement);
  }

  private fetchActive(): void {
    const f = this.activeFormat;
    if (f === 'youtube') return;
    if (this.states.get(f)?.url) return;
    if (this.states.get(f)?.loading) return;
    this.states.set(f, { loading: true });
    this.cdr.markForCheck();

    const sub = this.viewer.fetchAsBlobUrl(this.bundle, f, this.lang).subscribe({
      next: ({ url, langActuallyServed }) => {
        this.states.set(f, { url, loading: false, langActuallyServed });
        if (f === 'txt') this.fetchTextContent(url);
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.states.set(f, { loading: false, error: err?.message ?? 'Failed to load' });
        this.cdr.markForCheck();
      },
    });
    this.subscriptions.set(f, sub);
  }

  private fetchTextContent(_url: SafeResourceUrl): void {
    // Best-effort: re-fetch first 4KB through HttpClient. The blob URL is sandboxed; we just
    // ask the network again. For a v1 we render a placeholder until a richer outline ships.
    this.pendingTextContent = '— Outline preview —';
  }

  private releaseAll(): void {
    this.states.forEach((_, f) => {
      try { this.viewer.release(this.bundle, f, this.lang); } catch { /* noop */ }
      this.subscriptions.get(f)?.unsubscribe();
    });
    this.states.clear();
    this.subscriptions.clear();
  }
}
